/**
 * Brand-theme store — per-user color preset that overrides --primary / --accent / --ring
 * via a `data-brand` attribute on <html>. Presets live in `src/app/globals.css`.
 *
 * Persistence order on a fresh page load:
 *   1. Inline boot script in `layout.tsx` reads localStorage and sets the attribute
 *      BEFORE first paint (no flash of wrong color).
 *   2. <BrandThemeProvider> on mount calls hydrateFromBackend() — if the server
 *      has a saved value it wins, and localStorage is updated to match.
 *
 * Writes:
 *   setBrand() updates state + DOM attribute + localStorage immediately, then
 *   fires-and-forgets a PATCH to /api/v1/user-preferences/context so it persists
 *   across devices.
 */

import { create } from 'zustand';
import { UserPreferencesService } from '@/lib/api/user-preferences-service';

export type BrandThemeId =
  | 'default'
  | 'indigo'
  | 'violet'
  | 'emerald'
  | 'rose'
  | 'amber'
  | 'slate';

export interface BrandThemeMeta {
  id: BrandThemeId;
  label: string;
  /** Hex sample used to render the swatch in the picker — light-mode primary. */
  swatchLight: string;
  /** Dark-mode counterpart used when the user is in dark theme. */
  swatchDark: string;
}

export const BRAND_THEMES: BrandThemeMeta[] = [
  { id: 'default',  label: 'Classic Blue', swatchLight: '#2563EB', swatchDark: '#3B82F6' },
  { id: 'indigo',   label: 'Indigo',       swatchLight: '#205dde', swatchDark: '#8eabf5' },
  { id: 'violet',   label: 'Violet',       swatchLight: '#205dde', swatchDark: '#8eabf5' },
  { id: 'emerald',  label: 'Emerald',      swatchLight: '#10B981', swatchDark: '#34D399' },
  { id: 'rose',     label: 'Rose',         swatchLight: '#E11D48', swatchDark: '#FB7185' },
  { id: 'amber',    label: 'Amber',        swatchLight: '#D97706', swatchDark: '#FBBF24' },
  { id: 'slate',    label: 'Slate',        swatchLight: '#334155', swatchDark: '#94A3B8' },
];

const VALID_IDS = new Set<BrandThemeId>(BRAND_THEMES.map((t) => t.id));
export const BRAND_STORAGE_KEY = 'nue-brand-theme';

const isValid = (v: unknown): v is BrandThemeId =>
  typeof v === 'string' && VALID_IDS.has(v as BrandThemeId);

const applyToDom = (brand: BrandThemeId) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (brand === 'default') {
    root.removeAttribute('data-brand');
  } else {
    root.setAttribute('data-brand', brand);
  }
};

interface BrandThemeState {
  brand: BrandThemeId;
  hydrated: boolean;
  setBrand: (brand: BrandThemeId) => void;
  hydrateFromBackend: () => Promise<void>;
}

export const useBrandThemeStore = create<BrandThemeState>((set, get) => ({
  brand: 'default',
  hydrated: false,

  setBrand: (brand) => {
    if (!isValid(brand)) return;
    set({ brand });
    applyToDom(brand);
    if (typeof window !== 'undefined') {
      try { localStorage.setItem(BRAND_STORAGE_KEY, brand); } catch { /* private mode */ }
    }
    // Fire-and-forget — failure to persist on the server is non-blocking.
    UserPreferencesService.updateUserContext({ brand_theme: brand }).catch(() => {});
  },

  hydrateFromBackend: async () => {
    if (typeof window === 'undefined') return;

    // Seed from localStorage first so the local view is always in sync even
    // before the server responds. The inline boot script in layout.tsx has
    // already applied the DOM attribute — this just syncs Zustand state.
    let local: BrandThemeId = 'default';
    try {
      const raw = localStorage.getItem(BRAND_STORAGE_KEY);
      if (isValid(raw)) local = raw;
    } catch { /* ignore */ }
    set({ brand: local });

    try {
      const ctx = await UserPreferencesService.getUserContext();
      const backend = ctx?.brand_theme;
      if (isValid(backend) && backend !== get().brand) {
        set({ brand: backend });
        applyToDom(backend);
        try { localStorage.setItem(BRAND_STORAGE_KEY, backend); } catch { /* ignore */ }
      }
    } catch {
      // Not authenticated yet, or backend unavailable. The local value sticks.
    } finally {
      set({ hydrated: true });
    }
  },
}));
